import { useState, useEffect, useRef } from 'react'
import { useParentCommentSync } from '../hooks/useParentCommentSync'
import { useParentComments } from '../hooks/useParentComments'

/**
 * 保護者連絡帳コンポーネント
 *
 * - 日付範囲を指定して CoDMON から連絡帳を同期
 * - コメントを最新順に表示し、スクロール/ボタンで続きを読み込む
 */
export function ParentCommentSync() {
  const today = new Date().toISOString().split('T')[0]!
  const thirtyDaysAgo = new Date(Date.now() - 30 * 24 * 60 * 60 * 1000)
    .toISOString()
    .split('T')[0]!

  const [startDate, setStartDate] = useState(thirtyDaysAgo)
  const [endDate, setEndDate] = useState(today)

  const { syncing, syncError, syncResult, sync } = useParentCommentSync()
  const { data, loading, error, loadMore, refetch } = useParentComments()
  const loadMoreRef = useRef<HTMLDivElement>(null)

  // 無限スクロール: Intersection Observerで「さらに読み込む」ボタン付近を監視
  useEffect(() => {
    if (!loadMoreRef.current || !data?.nextCursor || loading) {
      return
    }

    const observer = new IntersectionObserver(
      (entries) => {
        // ボタンが画面に表示されたら自動的に読み込む
        if (entries[0]?.isIntersecting) {
          loadMore()
        }
      },
      {
        // ボタンが画面下から100px手前に来たら発火
        rootMargin: '100px',
      }
    )

    observer.observe(loadMoreRef.current)

    return () => {
      observer.disconnect()
    }
  }, [data?.nextCursor, loading, loadMore])

  const handleSync = () => {
    if (startDate && endDate) {
      // 同期後は先頭から読み込み直す
      sync(startDate, endDate, refetch)
    }
  }

  return (
    <div className="parent-comment-sync">
      <h2>連絡帳</h2>

      <div className="parent-comment-sync-form">
        <div className="date-filter-inputs">
          <input
            type="date"
            className="date-input"
            value={startDate}
            onChange={(e) => setStartDate(e.target.value)}
            disabled={syncing}
          />
          <span className="date-separator">〜</span>
          <input
            type="date"
            className="date-input"
            value={endDate}
            onChange={(e) => setEndDate(e.target.value)}
            disabled={syncing}
          />
        </div>
        <button
          className="button button-primary"
          onClick={handleSync}
          disabled={syncing || !startDate || !endDate}
        >
          {syncing ? '同期中...' : '同期する'}
        </button>
      </div>

      {syncError && <div className="error-message">{syncError}</div>}

      {syncResult && (
        <div className="parent-comment-sync-result">
          {syncResult.saved}件のコメントを保存しました（{syncResult.start_date} 〜 {syncResult.end_date}）
        </div>
      )}

      {loading && !data && <div className="loading">読み込み中...</div>}

      {error && <div className="error-message">{error}</div>}

      {!error && data && data.comments.length === 0 && (
        <div className="empty-state">コメントがありません。</div>
      )}

      {!error && data && data.comments.length > 0 && (
        <>
          <div className="parent-comment-items">
            {data.comments.map((comment) => (
              <div key={comment.id} className="parent-comment-item">
                <div className="parent-comment-item-date">{comment.display_date}</div>
                <div className="parent-comment-item-memo">{comment.memo}</div>
              </div>
            ))}
          </div>

          {data.nextCursor && !loading && (
            <div ref={loadMoreRef} className="load-more-container">
              <button onClick={loadMore} className="load-more-button">
                さらに読み込む
              </button>
            </div>
          )}

          {loading && (
            <div className="loading-more">
              <p>読み込み中...</p>
            </div>
          )}
        </>
      )}
    </div>
  )
}
